import { BizAppConfig } from 'config/application.manifest';
import { createAppRequest, createRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';
import type {
  CreateUserFeedbackPayload,
  GetFeedbackBusinessApplicationsResult,
  GetUserFeedbackListParams,
  GetUserFeedbackListResult,
  SubmitterFeedbackDetail,
  UserFeedbackDetail,
  UserFeedbackRecord,
} from './types/feedback';

const request = createAppRequest();
const metaPlatformRequest = createRequest(BizAppConfig.appMetaPlatformUrl);
const FEEDBACK_APP_TAXONOMY_UUID = '6a363e07-f64b-40db-9b74-019d3a36c083';

/**
 * 提交用户反馈
 */
export async function createUserFeedback(
  body: CreateUserFeedbackPayload,
  options?: ApiRequestOptions,
) {
  return request<UserFeedbackRecord>('/bff/feedback', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/**
 * 获取我的反馈
 */
export async function getMyFeedbackList(
  params?: GetUserFeedbackListParams,
  options?: ApiRequestOptions,
) {
  return request<GetUserFeedbackListResult>('/bff/feedback/mine', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export async function getAllFeedbackList(
  params?: GetUserFeedbackListParams,
  options?: ApiRequestOptions,
) {
  return request<GetUserFeedbackListResult>('/bff/feedback/all', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/**
 * 获取当前租户下的全部反馈。
 * 与旧的跨租户接口并存，避免影响已有调用方。
 */
export async function getTenantFeedbackList(
  params?: GetUserFeedbackListParams,
  options?: ApiRequestOptions,
) {
  return request<GetUserFeedbackListResult>('/bff/feedback/tenant', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/**
 * 获取反馈详情
 */
export async function getUserFeedbackDetail(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<UserFeedbackDetail>(`/bff/feedback/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/**
 * 获取当前租户下的反馈详情。
 */
export async function getTenantFeedbackDetail(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<UserFeedbackDetail>(`/bff/feedback/tenant/${uuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getSubmitterFeedbackDetail(
  uuid: string,
  options?: ApiRequestOptions,
) {
  return request<SubmitterFeedbackDetail>(`/bff/feedback/${uuid}/public`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function supplementFeedback(
  uuid: string,
  body: { content: string; expectedRevision: number; requestId: string },
  files: File[] = [],
  options?: ApiRequestOptions,
) {
  const data = new FormData();
  data.append('content', body.content);
  data.append('expectedRevision', String(body.expectedRevision));
  data.append('requestId', body.requestId);
  files.forEach(file => data.append('files', file));
  return request<import('./types/feedback').FeedbackReplyRecord>(
    '/bff/feedback/' + encodeURIComponent(uuid) + '/supplements',
    { method: 'POST', data, skipErrorHandler: true, ...(options || {}) },
  );
}

export async function withdrawFeedback(
  uuid: string, expectedRevision: number, options?: ApiRequestOptions,
) {
  return request<UserFeedbackRecord>(
    '/bff/feedback/' + encodeURIComponent(uuid) + '/withdraw',
    { method: 'POST', data: { expectedRevision }, skipErrorHandler: true, ...(options || {}) },
  );
}

export async function markFeedbackRead(uuid: string, options?: ApiRequestOptions) {
  return request<{ success: boolean }>(`/bff/feedback/${uuid}/read`, {
    method: 'POST', ...(options || {}),
  });
}

/**
 * 上传反馈附件
 */
export async function uploadFeedbackAttachment(
  uuid: string,
  file: File,
  options?: ApiRequestOptions,
) {
  const formData = new FormData();
  formData.append('file', file);
  return request<import('./types/feedback').FeedbackAttachmentRecord>(`/bff/feedback/${uuid}/attachments`, {
    method: 'POST',
    data: formData,
    ...(options || {}),
  });
}

export async function getFeedbackAttachmentUrl(
  uuid: string,
  attachmentUuid: string,
  mode: 'preview' | 'download',
  options?: ApiRequestOptions,
) {
  return request<{ url: string; urlMode: 'public_direct' | 'signed' }>(
    `/bff/feedback/${uuid}/attachments/${attachmentUuid}/${mode}`,
    { method: 'GET', ...(options || {}) },
  );
}

/**
 * 获取可反馈的业务应用列表
 */
export async function getFeedbackBusinessApplications(
  options?: ApiRequestOptions,
) {
  return metaPlatformRequest<GetFeedbackBusinessApplicationsResult>(
    '/bff/meta-platform/business-applications/workbench/grouped',
    {
      method: 'GET',
      params: {
        taxonomyUuid: FEEDBACK_APP_TAXONOMY_UUID,
        scenePlatformUuid: BizAppConfig.appPlatformUuid,
      },
      ...(options || {}),
    },
  );
}
